import { Group } from 'fabric';
import { assembleSpecs } from '../assemble';
import { underlineSlot } from '../kits/slotKit';
import type { PrimitiveSpec, TextMeasurer, TextSpec } from '../kits/specs';
import { estimateMeasure, translateSpecs } from '../kits/specs';
import { registerRecipe } from '../registry';
import { tagSmartObject } from '../types';
import type { ContentSourceSpec, StandardContentPayload, ToolRecipe } from '../types';

/**
 * Fill-in-the-blank — one sentence with a single write-on line between a
 * lead-in and a tail (CATALOG F2, slotKit.underlineSlot). "The capital of
 * France is ____ ." The three texts each render exactly one scalar param, so
 * every one is dblclick-editable on canvas for free (assemble.ts stamps the
 * {kind:'param'} engine tag).
 *
 * THE PRINT-KEY LAW (slotKit): the answer text is ALWAYS emitted — hidden as
 * visible:false when showAnswer is off — so the answer survives in the design
 * JSON and the teacher's "answer key" print is one toggle, not a retype. On
 * the worksheet the blank prints empty; on the key the answer rides ABOVE the
 * line in a distinct color.
 *
 * THE SLOT-WIDTH CHOICE: 'fit' lines size to the stored answer (a blank is
 * never a dot — minWidth floors it), so the line never gives less room than
 * the word needs; 'fixed' gives every blank the same teacher-chosen width
 * regardless of answer length (uniform worksheet columns, and it doesn't leak
 * the answer's length).
 *
 * Emits FabricText + Line only, grouped and tagged {tool,version,params} —
 * plain fabric JSON, vector at any export dpi by construction.
 */

// ---------------------------------------------------------------------------
// Defensive param coercion (the handwriting.ts pattern) — params arrive
// untrusted (user-typed or replayed from stored JSON); generate() must never
// throw.
// ---------------------------------------------------------------------------

function num(v: unknown, fallback: number): number {
  const n = typeof v === 'string' && v.trim() !== '' ? Number(v) : (v as number);
  return typeof n === 'number' && Number.isFinite(n) ? n : fallback;
}

function str(v: unknown, fallback: string): string {
  return typeof v === 'string' && v.trim() !== '' ? v : fallback;
}

/** Unlike str(), an empty string is a MEANINGFUL value here (= blank text). */
function rawStr(v: unknown, fallback: string): string {
  return typeof v === 'string' ? v : fallback;
}

function bool(v: unknown, fallback: boolean): boolean {
  if (typeof v === 'boolean') return v;
  if (v === 'true') return true;
  if (v === 'false') return false;
  return fallback;
}

const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

// ---------------------------------------------------------------------------
// Text measurement — canvas TextMetrics when available (insert and Apply both
// ensureFont the family first, so the measure sees the real face), estimate
// fallback elsewhere (SSR/node safety). Mirrors fraction.ts.
// ---------------------------------------------------------------------------

function makeMeasurer(family: string): TextMeasurer {
  try {
    const ctx = document.createElement('canvas').getContext('2d');
    if (!ctx) return estimateMeasure;
    return (text, fontSize) => {
      ctx.font = `${fontSize}px "${family}", sans-serif`;
      const w = ctx.measureText(text).width;
      return Number.isFinite(w) && w >= 0 ? w : estimateMeasure(text, fontSize);
    };
  } catch {
    return estimateMeasure;
  }
}

// ---------------------------------------------------------------------------
// Recipe
// ---------------------------------------------------------------------------

const INK = '#1e293b';
/** Classic teacher-key ink — distinct from the sentence so the answer reads
 *  as an overlay, not part of the printed prompt. */
const ANSWER_INK = '#dc2626';
const DEFAULT_FONT = 'Arial';
const FONT_SIZE = 48;

/** A tail like "." or "?" hugs the blank; a word tail gets a normal space. */
const HUGS_LEFT = /^[.,;:!?)\]}»”’%]/;

// ---------------------------------------------------------------------------
// Smart-fill seam (SM-H) — one sentence, one blank: take the best vocab item
// whose example sentence contains its term and split the example around it.
// ---------------------------------------------------------------------------

/** Escape a term for literal use inside a RegExp. */
function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * First WHOLE-WORD, case-insensitive occurrence of `term` in `text`. Whole-word
 * = not flanked by an ASCII letter/digit (reliable for multi-word / hyphenated
 * terms where \b is not). Returns the match span, or null when absent.
 */
function findTermSpan(text: string, term: string): { start: number; end: number } | null {
  const t = term.trim();
  if (!t || !text) return null;
  const re = new RegExp(`(?<![A-Za-z0-9])${escapeRegExp(t)}(?![A-Za-z0-9])`, 'i');
  const m = re.exec(text);
  return m ? { start: m.index, end: m.index + m[0].length } : null;
}

const contentSource: ContentSourceSpec = {
  kind: 'standard',
  wants: ['vocabulary'],
  applyContent(payload: StandardContentPayload, current: Record<string, unknown>) {
    // Merge INTO a copy so the educator's styling params (slotWidth, color,
    // font, size, showAnswer) survive; only before/answer/after + provenance
    // are touched — and the sentence trio ONLY when a usable example lands.
    const next = { ...current };
    const vocab = Array.isArray(payload?.vocabulary) ? payload.vocabulary : [];

    // Pick the FIRST vocab item whose example sentence contains its term
    // (whole-word). Split the example: before | blank | after; term = answer.
    for (const v of vocab) {
      const term = (v?.term ?? '').trim();
      const example = (v?.example ?? '').trim();
      if (!term || !example) continue;
      const span = findTermSpan(example, term);
      if (!span) continue;
      next.before = example.slice(0, span.start).trim();
      next.after = example.slice(span.end).trim();
      next.answer = term;
      break;
    }
    // No vocab item has a usable example → leave before/answer/after UNCHANGED.

    // Provenance (declared advanced schema fields; advanced so a provenance-only
    // change doesn't misfire the modal's "changed" detection).
    if (payload?.standard?.code) next.sourceStandardCode = payload.standard.code;
    if (payload?.standard?.id) next.sourceStandardId = payload.standard.id;
    return next;
  },
};

const fillBlank: ToolRecipe = {
  id: 'fill-blank',
  domain: 'ela',
  tags: ['fill-in-blank', 'worksheet', 'practice', 'answer-key', 'sentence', 'assessment'],
  name: 'Fill-in blank',
  category: 'practice',
  version: 1,
  description:
    'A sentence with one write-on line — "The capital of France is ____." Toggle the answer to print a key. Double-click any part on the poster to edit it.',
  defaults: {
    before: 'The capital of France is',
    answer: 'Paris',
    after: '.',
    showAnswer: false,
    slotWidth: 'fit',
    color: INK,
    fontFamily: DEFAULT_FONT,
    fontSize: FONT_SIZE,
    sourceStandardCode: '',
    sourceStandardId: '',
  },
  schema: [
    {
      key: 'before',
      label: 'Text before the blank',
      type: 'text',
      help: 'The start of the sentence, up to the blank.',
    },
    {
      key: 'answer',
      label: 'Answer',
      type: 'text',
      help: 'The word that belongs on the line. It stays saved even when hidden.',
    },
    {
      key: 'after',
      label: 'Text after the blank',
      type: 'text',
      help: 'The rest of the sentence. Punctuation like "." hugs the blank.',
    },
    {
      key: 'showAnswer',
      label: 'Show the answer (answer key)',
      type: 'boolean',
      help: 'On prints the answer above the line; off leaves a blank line to fill in.',
    },
    {
      key: 'slotWidth',
      label: 'Blank width',
      type: 'select',
      options: [
        { value: 'fit', label: 'Fit the answer' },
        { value: 'fixed', label: 'Fixed width' },
      ],
      section: 'Style',
    },
    { key: 'color', label: 'Color', type: 'color', section: 'Style' },
    { key: 'fontFamily', label: 'Font', type: 'font', section: 'Style' },
    {
      key: 'fontSize',
      label: 'Text size',
      type: 'number',
      min: 12,
      max: 240,
      step: 2,
      section: 'Style',
    },
    { key: 'sourceStandardCode', label: 'Source standard code', type: 'text', advanced: true, section: 'Style', help: 'Set automatically when filled from a standard.' },
    { key: 'sourceStandardId', label: 'Source standard id', type: 'text', advanced: true, section: 'Style', help: 'Set automatically when filled from a standard.' },
  ],
  contentSource,
  async generate(params) {
    const before = rawStr(params.before, '');
    const answer = rawStr(params.answer, '');
    const after = rawStr(params.after, '');
    const showAnswer = bool(params.showAnswer, false);
    const mode = str(params.slotWidth, 'fit') === 'fixed' ? 'fixed' : 'fit';
    const color = str(params.color, INK);
    const family = str(params.fontFamily, DEFAULT_FONT);
    const fontSize = clamp(num(params.fontSize, FONT_SIZE), 6, 400);

    const measure = makeMeasurer(family);
    const gap = fontSize * 0.4;

    const specs: PrimitiveSpec[] = [];
    let x = 0;

    // Lead-in text (skip an empty one — nothing to render or click; the param
    // still round-trips and the panel can re-fill it).
    if (before !== '') {
      specs.push({
        kind: 'text',
        text: before,
        x,
        y: 0,
        fontSize,
        fontFamily: family,
        fill: color,
        originX: 'left',
        originY: 'top',
        param: 'before',
      } satisfies TextSpec);
      x += measure(before, fontSize) + gap;
    }

    // The write-on line. 'fixed' gives a uniform teacher-chosen width; 'fit'
    // lets slotKit size it to the answer (never below its blank-floor).
    const slot = underlineSlot({
      answer,
      showAnswer,
      fontSize,
      color,
      answerColor: ANSWER_INK,
      fontFamily: family,
      measure,
      ...(mode === 'fixed' ? { width: fontSize * 6 } : {}),
      answerParam: 'answer',
    });
    // Align the slot's rule with the surrounding text baseline: the rule sits
    // at the text box bottom either way (underlineSlot.lineY == boxH + …), so
    // the blocks share y = 0.
    specs.push(...translateSpecs(slot.specs, x, 0));
    x += slot.width;

    // Tail text. Punctuation hugs the blank; a following word gets a space.
    if (after !== '') {
      const lead = HUGS_LEFT.test(after) ? 0 : gap;
      x += lead;
      specs.push({
        kind: 'text',
        text: after,
        x,
        y: 0,
        fontSize,
        fontFamily: family,
        fill: color,
        originX: 'left',
        originY: 'top',
        param: 'after',
      } satisfies TextSpec);
      x += measure(after, fontSize);
    }
    void x; // final cursor; Group derives the real bbox from the children.

    const group = new Group(assembleSpecs(specs));
    group.set({ left: 0, top: 0 });
    return tagSmartObject(group, fillBlank, params);
  },
};

registerRecipe(fillBlank);
